// Level generation and validation utilities for React
import type { Position } from '@/types/game';
import { findTraversalRouteToExit } from '@/game/levelTraversal';

export interface LevelData {
  level: string[];
  start: Coordinate;
  end: Coordinate;
  difficulty: number;
  complexity: number;
}

export interface Coordinate {
  x: number;
  y: number;
}

export interface LevelTestResult {
  level: number;
  solvable: boolean;
  complexity: number;
}

export interface PathNode {
  pos: number;
  path: number[];
}

export class PathfindingAlgorithm {
  findPath(level: string[], size: number, start: number, end: number): number[] | null {
    const visited = new Set<number>();
    const queue: PathNode[] = [{ pos: start, path: [start] }];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;

      const { pos, path } = current;

      if (pos === end) {
        return path;
      }

      if (visited.has(pos)) continue;
      visited.add(pos);

      const neighbors = this.getNeighbors(pos, size);

      for (const neighbor of neighbors) {
        if (!visited.has(neighbor) && this.isValidMove(level, neighbor)) {
          queue.push({
            pos: neighbor,
            path: [...path, neighbor],
          });
        }
      }
    }

    return null; // No path found
  }

  private getNeighbors(pos: number, size: number): number[] {
    const neighbors: number[] = [];
    const x = pos % size;
    const y = Math.floor(pos / size);

    // Up, Down, Left, Right
    if (y > 0) neighbors.push(pos - size);
    if (y < size - 1) neighbors.push(pos + size);
    if (x > 0) neighbors.push(pos - 1);
    if (x < size - 1) neighbors.push(pos + 1);

    return neighbors;
  }

  private isValidMove(level: string[], pos: number): boolean {
    const tile = level[pos];
    return tile !== 'R' && tile !== 'x' && tile !== 'X' && tile !== undefined;
  }
}

export class LevelUtils {
  private pathfinder: PathfindingAlgorithm;

  constructor() {
    this.pathfinder = new PathfindingAlgorithm();
  }

  // Simple seedable random number generator
  private seedRandom(seed: string | number): () => number {
    let state = typeof seed === 'string' ? this.hashCode(seed) : seed;
    const m = 0x80000000;
    const a = 1103515245;
    const c = 12345;

    return function (): number {
      state = (a * state + c) % m;
      return state / (m - 1);
    };
  }

  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  // Generate a solvable level with given parameters
  generateLevel(width = 29, difficulty = 1, seed?: string | number): LevelData {
    const random = seed ? this.seedRandom(seed) : Math.random;
    const size = width;
    const level: string[] = new Array(size * size).fill('-');

    // Place start and end positions
    const start = this.getRandomPosition(size, [], random);
    const end = this.getRandomPosition(size, [start], random);

    level[start] = 'o'; // Start (original character)
    level[end] = 'e'; // End (original character)

    // Generate obstacles based on difficulty
    this.placeObstacles(level, size, difficulty, [start, end], random);

    // Ensure the level is solvable
    if (!this.isLevelSolvable(level, size)) {
      return this.generateLevel(width, difficulty, seed); // Retry
    }

    return {
      level,
      start: this.indexToCoord(start, size),
      end: this.indexToCoord(end, size),
      difficulty,
      complexity: this.calculateComplexity(level),
    };
  }

  // Check if a level is solvable using pathfinding
  isLevelSolvable(level: string[], size: number): boolean {
    if (!level.length || size <= 0) return false;
    const grid = this.toGrid(level, size);
    return findTraversalRouteToExit(grid) !== null;
  }

  // Validate a puzzle string (from puzzle1.ts format)
  validatePuzzleString(puzzleString: string, width: number): boolean {
    if (puzzleString.length === 0) return false;

    const level = puzzleString.split('');
    return this.isLevelSolvable(level, width);
  }

  // Generate more levels for the game
  generateMoreLevels(count = 50, startDifficulty = 1): string[] {
    const levels: string[] = [];

    for (let i = 0; i < count; i++) {
      const difficulty = startDifficulty + Math.floor(i / 10);
      const seed = `level_${Date.now()}_${i}`;
      const levelData = this.generateLevel(29, difficulty, seed);
      levels.push(levelData.level.join(''));
    }

    return levels;
  }

  // Place obstacles in the level
  private placeObstacles(
    level: string[],
    size: number,
    difficulty: number,
    excludePositions: number[],
    random: () => number = Math.random
  ): void {
    const obstacleCount = Math.floor(size * size * 0.1 * difficulty);
    const obstacles = ['x', 'l', 's']; // Rock (x), Lava (l), Stop (s)

    for (let i = 0; i < obstacleCount; i++) {
      let pos: number;
      do {
        pos = this.getRandomPosition(size, [], random);
      } while (excludePositions.includes(pos) || level[pos] !== '-');

      const obstacleType = obstacles[Math.floor(random() * obstacles.length)];
      level[pos] = obstacleType;
    }
  }

  // Get random position in grid
  private getRandomPosition(
    size: number,
    exclude: number[] = [],
    random: () => number = Math.random
  ): number {
    let pos: number;
    do {
      pos = Math.floor(random() * size * size);
    } while (exclude.includes(pos));
    return pos;
  }

  // Convert index to coordinates
  indexToCoord(index: number, size: number): Coordinate {
    return {
      x: index % size,
      y: Math.floor(index / size),
    };
  }

  // Convert coordinates to index
  coordToIndex(coord: Coordinate, size: number): number {
    return coord.y * size + coord.x;
  }

  // Calculate level complexity
  calculateComplexity(level: string[]): number {
    let complexity = 0;
    for (let i = 0; i < level.length; i++) {
      const cell = level[i];
      if (cell !== '-' && cell !== 'o' && cell !== 'O' && cell !== 'e' && cell !== 'E') {
        complexity++;
      }
    }
    return complexity;
  }

  // Test all levels for solvability
  testAllLevels(levels: string[]): LevelTestResult[] {
    const results: LevelTestResult[] = [];

    for (let i = 0; i < levels.length; i++) {
      const levelArray = levels[i].split('');
      const isValid = this.isLevelSolvable(levelArray, 29);
      
      results.push({
        level: i,
        solvable: isValid,
        complexity: this.calculateComplexity(levelArray),
      });

      if (!isValid) {
        console.warn(`Level ${i} is not solvable!`);
      }
    }

    return results;
  }

  // Find optimal path for hint system
  findOptimalPath(puzzleString: string, width: number): Position[] | null {
    if (!puzzleString || width <= 0) return null;
    const rows = this.stringToGrid(puzzleString, width);
    const route = findTraversalRouteToExit(rows);
    if (!route) return null;
    return route.stops.map((stop) => [stop[0], stop[1]]);
  }

  // Get level statistics
  getLevelStats(puzzleString: string, width: number): {
    solvable: boolean;
    complexity: number;
    pathLength: number | null;
    obstacleCount: number;
    emptySpaces: number;
  } {
    const level = puzzleString.split('');
    const solvable = this.isLevelSolvable(level, width);
    const complexity = this.calculateComplexity(level);
    
    let pathLength: number | null = null;
    if (solvable) {
      const path = this.findOptimalPath(puzzleString, width);
      pathLength = path ? path.length : null;
    }

    const obstacleCount = level.filter(cell => 
      cell === 'x' || cell === 'X' || cell === 'l' || cell === 'L' || cell === 's' || cell === 'S' || cell === 'n' || cell === 'N'
    ).length;

    const emptySpaces = level.filter(cell => cell === '-').length;

    return {
      solvable,
      complexity,
      pathLength,
      obstacleCount,
      emptySpaces,
    };
  }

  // Difficulty analysis
  analyzeDifficulty(puzzleString: string, width: number): {
    difficulty: number;
    factors: {
      pathLength: number;
      obstacleRatio: number;
      complexity: number;
    };
  } {
    const stats = this.getLevelStats(puzzleString, width);
    const totalCells = width * width;
    
    const pathLength = stats.pathLength || 0;
    const obstacleRatio = stats.obstacleCount / totalCells;
    const complexity = stats.complexity;

    // Calculate difficulty based on various factors
    let difficulty = 1;
    
    // Path length factor (longer paths are generally easier)
    if (pathLength > 20) difficulty += 0.5;
    if (pathLength > 40) difficulty += 0.5;
    
    // Obstacle ratio factor
    if (obstacleRatio > 0.3) difficulty += 1;
    if (obstacleRatio > 0.5) difficulty += 1;
    
    // Complexity factor
    if (complexity > 50) difficulty += 1;
    if (complexity > 100) difficulty += 1;

    return {
      difficulty: Math.min(5, Math.max(1, Math.round(difficulty))),
      factors: {
        pathLength,
        obstacleRatio,
        complexity,
      },
    };
  }

  private toGrid(level: string[], size: number): string[] {
    const grid: string[] = [];
    for (let i = 0; i < level.length; i += size) {
      const row = level.slice(i, i + size).join('');
      if (row.length > 0) grid.push(row);
    }
    return grid;
  }

  private stringToGrid(level: string, width: number): string[] {
    const grid: string[] = [];
    for (let i = 0; i < level.length; i += width) {
      grid.push(level.slice(i, i + width));
    }
    return grid;
  }
}

/**
 * Compute every tile reachable via the game's sliding mechanics.
 *
 * Returns a Set of "col,row" keys for every tile the player can slide
 * through (including tiles they pass over but can't stop on).
 *
 * This mirrors the movement logic in useGameEngine's movePlayerInDirection:
 *   - The player slides along one axis until hitting a rock/edge.
 *   - Lava blocks like rock (if no shield), stop tiles stop you ON the tile,
 *     and teleporter tiles stop you ON the tile then warp you.
 *   - Every tile along the slide path (not just the stop tile) is reachable.
 */
export function computeReachableTiles(
  puzzleGrid: string[],
  teleporterMap?: Map<string, Position>,
): Set<string> {
  const rows = puzzleGrid.length;
  const reachable = new Set<string>();
  const visited = new Set<string>(); // stoppable positions already explored

  // Parse grid into obstacle map
  type ObstacleType = 'rock' | 'lava' | 'stop' | 'teleporter';
  const obstacles = new Map<string, ObstacleType>();
  let startPos: Position = [0, 0];
  let exitPos: Position | null = null;

  puzzleGrid.forEach((row, y) => {
    row.split('').forEach((cell, x) => {
      const lower = cell.toLowerCase();
      if (lower === 'x') obstacles.set(`${x},${y}`, 'rock');
      else if (lower === 'l') obstacles.set(`${x},${y}`, 'lava');
      else if (lower === 's') obstacles.set(`${x},${y}`, 'stop');
      else if (lower === 'n') obstacles.set(`${x},${y}`, 'stop');
      else if (lower === 't') obstacles.set(`${x},${y}`, 'teleporter');
      else if (lower === 'o') startPos = [x, y];
      else if (lower === 'e') exitPos = [x, y];
    });
  });

  const getCols = (row: number) => puzzleGrid[row]?.length || 0;

  // Simulate one slide from `pos` in the given direction.
  // Returns { stopPos, pathTiles } where pathTiles includes all tiles traversed
  // (excluding start, including stop) but NOT tiles beyond an obstacle.
  const slide = (
    pos: Position,
    direction: 'up' | 'down' | 'left' | 'right',
  ): { stopPos: Position; pathTiles: Position[] } => {
    const axis = direction === 'left' || direction === 'right' ? 1 : 0;
    const perp = axis === 0 ? 1 : 0;
    const forward = direction === 'down' || direction === 'right';

    const posArr: [number, number] = [pos[0], pos[1]];

    // Collect rocks on the same row/column in the direction of travel
    const relevantObstacles: Array<{ pos: Position; type: ObstacleType }> = [];
    for (const [key, type] of obstacles) {
      const [ox, oy] = key.split(',').map(Number);
      const obstPos: Position = [ox, oy];
      if (obstPos[axis] !== posArr[axis]) continue;
      if (forward ? obstPos[perp] > posArr[perp] : obstPos[perp] < posArr[perp]) {
        relevantObstacles.push({ pos: obstPos, type });
      }
    }

    let stopPerp: number;
    let landOnObstacle = false;

    if (relevantObstacles.length > 0) {
      const minmax = forward
        ? Math.min(...relevantObstacles.map(o => o.pos[perp]))
        : Math.max(...relevantObstacles.map(o => o.pos[perp]));
      const hitObstacle = relevantObstacles.find(o => o.pos[perp] === minmax)!;

      if (hitObstacle.type === 'rock') {
        stopPerp = forward ? minmax - 1 : minmax + 1;
      } else if (hitObstacle.type === 'lava') {
        // Assume no shield for reachability (conservative: treat like rock)
        stopPerp = forward ? minmax - 1 : minmax + 1;
      } else {
        // stop, teleporter: land ON it
        stopPerp = minmax;
        landOnObstacle = true;
      }
    } else {
      // No obstacles: slide to edge
      const maxVal = axis === 1 ? getCols(posArr[1]) - 1 : rows - 1;
      stopPerp = forward ? maxVal : 0;
    }

    // Build path tiles (all tiles between start exclusive and stop inclusive)
    const pathTiles: Position[] = [];
    const step = forward ? 1 : -1;
    for (
      let p = posArr[perp] + step;
      forward ? p <= stopPerp : p >= stopPerp;
      p += step
    ) {
      const tile: Position = [posArr[0], posArr[1]];
      tile[perp] = p;
      pathTiles.push(tile);
    }

    const stopPos: Position = [posArr[0], posArr[1]];
    stopPos[perp] = stopPerp;

    return { stopPos, pathTiles };
  };

  // BFS over stoppable positions
  const queue: Position[] = [startPos];
  const key = (p: Position) => `${p[0]},${p[1]}`;

  visited.add(key(startPos));
  reachable.add(key(startPos));

  while (queue.length > 0) {
    const current = queue.shift()!;

    for (const dir of ['up', 'down', 'left', 'right'] as const) {
      const { stopPos, pathTiles } = slide(current, dir);

      // Mark all path tiles as reachable
      for (const tile of pathTiles) {
        reachable.add(key(tile));
      }

      // If no movement, skip
      if (stopPos[0] === current[0] && stopPos[1] === current[1]) continue;

      // The exit tile is reachable but the game ends there -
      // don't explore further from it.
      const isExit = (p: Position) =>
        exitPos !== null && p[0] === exitPos[0] && p[1] === exitPos[1];

      // Check if we landed on a teleporter
      let finalPos = stopPos;
      if (teleporterMap) {
        const dest = teleporterMap.get(key(stopPos));
        if (dest) {
          finalPos = [dest[0], dest[1]];
          reachable.add(key(finalPos));
        }
      }

      // Explore from the final position if not already visited
      // (but never explore from the exit - the level ends there)
      if (!visited.has(key(finalPos))) {
        visited.add(key(finalPos));
        reachable.add(key(finalPos));
        if (!isExit(finalPos)) {
          queue.push(finalPos);
        }
      }

      // Also explore from the stop position (before teleport) if different
      if (key(stopPos) !== key(finalPos) && !visited.has(key(stopPos))) {
        visited.add(key(stopPos));
        if (!isExit(stopPos)) {
          queue.push(stopPos);
        }
      }
    }
  }

  return reachable;
}

export default LevelUtils;
